import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';
import { API_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me`);
      setUser(response.data);
      // Update i18n language based on user settings
      if (response.data.settings?.language) {
        i18n.changeLanguage(response.data.settings.language);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch full user data to ensure we have all fields including role
      const fullUserResponse = await axios.get(`${API_URL}/users/me`);
      setUser(fullUserResponse.data);
      
      // Update i18n language based on user settings
      if (fullUserResponse.data.settings?.language) {
        i18n.changeLanguage(fullUserResponse.data.settings.language);
      }
      
      return { success: true, user: fullUserResponse.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        code: error.response?.data?.code
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      const { token, user, message } = response.data;
      // After registration we require email verification; backend does not return token until verified
      if (token) {
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        return { success: true };
      }
      return { success: true, requiresVerification: true, message, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        errors: error.response?.data?.errors
      };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/auth/verify-email`, { params: { token } });
      const { token: jwtToken, user: userData } = response.data;
      localStorage.setItem('token', jwtToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
      const fullUserResponse = await axios.get(`${API_URL}/users/me`);
      setUser(fullUserResponse.data);
      if (fullUserResponse.data.settings?.language) {
        i18n.changeLanguage(fullUserResponse.data.settings.language);
      }
      return { success: true, user: fullUserResponse.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Verification failed'
      };
    }
  };

  const logout = (onLogoutComplete) => {
    localStorage.removeItem('token');
    localStorage.removeItem('cart');
    localStorage.removeItem('currentDeliveryAddress');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setLoading(false);
    // Call callback if provided (e.g., to clear cart and address contexts)
    if (onLogoutComplete) {
      onLogoutComplete();
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        verifyEmail,
        logout,
        updateUser,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
